/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import configuration */
import {
    GENERAL_STATUS_CRITICAL,
    GENERAL_STATUS_WARNING,
} from '@/config/app';
import { getPvPredictionStatus } from '@/config/kpi/statusFunctions';

/* Import libraries */
import { roundToSignificantDigits } from '@/lib/formatting/significantDigits';
import {DeviceSensor} from "@/lib/sensors/catalog/deviceSensor";

type Translate = (key: string, values?: Record<string, number | string>) => string;

interface PvPredictionDetailKpiConfigOptions {
    deviceSensor: DeviceSensor;
    t: Translate;
}

/** Creates the PV outage KPI displayed above the sensor history chart. */
export function createPvPredictionDetailKpiConfigs({
    deviceSensor,
    t,
}: PvPredictionDetailKpiConfigOptions) {

    const outageDtcSensorName = deviceSensor.getSensorName('PV', 'outage.dtc');
    const redispatchEnergySensorName = deviceSensor.getSensorName('PV', 'redispatch.energy_30');
    const yearlyLossSensorName = deviceSensor.getSensorName('PV', 'loss.360_rel');
    const yearlyLossDtcSensorName = deviceSensor.getSensorName('PV', 'loss.dtc');

    if (
        outageDtcSensorName === null ||
        redispatchEnergySensorName === null ||
        yearlyLossSensorName === null ||
        yearlyLossDtcSensorName === null
    ) {
        return null;
    }

    return {
        outage: {
            aggregation: 'max' as const,
            calc: ({ values }: { values: Record<string, number | null> }) => ({
                'calculated.status': getPvPredictionStatus(values[outageDtcSensorName]),
            }),
            calcCode: `({ values }) => ({
    // Converts the maximum outage DTC to [0|1|2]
    'calculated.status': getPvPredictionStatus(values['${outageDtcSensorName}']),
})`,
            legendSeries: outageDtcSensorName,
            legendTrafficLight: {
                rules: [
                    { severity: 'danger' as const, value: 200, when: 'gte' as const },
                    { severity: 'warning' as const, value: 100, when: 'gte' as const },
                ],
                fallback: 'success' as const,
            },
            from: 'NOW-30DAYS',
            series: 'calculated.status',
            showLegend: true,
            title: t('Outage in last 30 days'),
            to: 'NOW',
            trafficLight: {
                rules: [
                    { severity: 'danger' as const, value: GENERAL_STATUS_CRITICAL, when: 'gte' as const },
                    { severity: 'warning' as const, value: GENERAL_STATUS_WARNING, when: 'gte' as const },
                ],
                fallback: 'success' as const,
            },
            urn: outageDtcSensorName,
            valueLabels: {
                values: {
                    '0': t('No outage'),
                    '1': t('Minor outage'),
                    '2': t('Significant outage'),
                },
                fallback: '-',
            },
        },
        redispatch: {
            aggregation: 'last' as const,
            conversion: 1,
            from: 'NOW-30DAYS',
            legendCode: `// Uses the latest thirty-day energy loss directly in kWh
last(timeSeriesValues['${redispatchEnergySensorName}'])`,
            showLegend: true,
            title: t('Redispatch in last 30 days'),
            to: 'NOW',
            trafficLight: {
                rules: [
                    { severity: 'danger' as const, value: 0, when: 'gt' as const },
                ],
                fallback: 'success' as const,
            },
            transformValue: (value: number) => roundToSignificantDigits(value, 2),
            trimFractionDigitsForIntegers: true,
            unit: 'kWh',
            urn: redispatchEnergySensorName,
        },
        yearlyLoss: {
            aggregation: 'last' as const,
            display: {
                series: yearlyLossSensorName,
                unit: '%',
            },
            from: 'NOW-30DAYS',
            legendCode: `// Displayed percentage: last yearly loss value, limited to 0 %
const yearlyLossPercent = Math.max(0, last(timeSeriesValues['${yearlyLossSensorName}']));

// Traffic-light color: last DTC value
const lossDtc = last(timeSeriesValues['${yearlyLossDtcSensorName}']);
const trafficLight = lossDtc >= 200 ? 'error' : lossDtc >= 100 ? 'warning' : 'ok';`,
            legendNote: t('Displayed percentage source: {valueSensor}. Traffic light source: {statusSensor}.', {
                valueSensor: yearlyLossSensorName,
                statusSensor: yearlyLossDtcSensorName,
            }),
            series: yearlyLossDtcSensorName,
            showDate: true,
            showLegend: true,
            title: t('Energy loss difference (1 year)'),
            to: 'NOW',
            trafficLight: {
                rules: [
                    { severity: 'danger' as const, value: 200, when: 'gte' as const },
                    { severity: 'warning' as const, value: 100, when: 'gte' as const },
                ],
                fallback: 'success' as const,
            },
            transformValue: (value: number) => roundToSignificantDigits(Math.max(0, value), 2),
            urn: [yearlyLossSensorName, yearlyLossDtcSensorName],
        },
    };
}
